import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import heroBg from "../assets/herobg.png";
import aboutImage from "../assets/hari.jpeg";
import { TbBrandAdobe } from "react-icons/tb";
import { ReactComponent as FigmaLogo } from "devicon/icons/figma/figma-original.svg";
import { ReactComponent as CanvaLogo } from "devicon/icons/canva/canva-original.svg";
import { ReactComponent as MatlabLogo } from "devicon/icons/matlab/matlab-original.svg";

import { F } from "../fonts";
import { useBuilds, useDesigns, useAchievements, useCertifications, useResume, trackVisit, trackResumeDownload, trackBuildClick } from "../api";

import proj1 from "../assets/UIUX.png";
import proj2 from "../assets/graphic.png";

// ─── SVG Icons ────────────────────────────────────────────────────────────────


const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="17" height="17" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L2.002 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117Z" />
  </svg>
);

// ─── Software Icons ────────────────────────────────────────────────────────────

const FigmaIcon = () => <FigmaLogo width={18} height={18} />;
const CanvaIcon = () => <CanvaLogo width={18} height={18} />;
const MatlabIcon = () => <MatlabLogo width={18} height={18} />;
const AdobeIcon = () => <TbBrandAdobe size={18} color="#FA0F00" />;

// ─── Color Palette ────────────────────────────────────────────────────────────
const C = {
  vanilla:  "#F8F4EE",   // page background (light cream)
  softDune: "#E4DDCC",   // secondary bg, marquee, accordion
  taupe:    "#C5B49D",   // borders, muted elements, icon bg
  umber:    "#443A35",   // dark sections, footer bg
  black:    "#252525",   // text, borders, icons
};

// ─── Data ─────────────────────────────────────────────────────────────────────

const marqueeSkills = ["PHOTOSHOP", "CANVA", "FIGMA", "BALSAMIQ", "ILLUSTRATOR", "C", "PYTHON", "JAVASCRIPT", "REACT.JS", "HTML5", "CSS3", "FLASK", "NODE.JS", "EXPRESS.JS", "MONGODB", "MYSQL", "GIT"];

// corner = card's position in the 2×2 grid (tl/tr = top row, bl/br = bottom row)
// Arc is always cut at the INNER corner of each card (the corner facing grid center)
const projects = [
  { id: 1, name: "UI/UX designs", description: "UI/UX redesign for a mobile app", image: proj1, route: "/uiux", corner: "tl" },
  { id: 2, name: "Poster designs", description: "Brand identity & visual system",  image: proj2, route: "/posters", corner: "tr" },
];



// ─── Arc Cutout Component ─────────────────────────────────────────────────────
// Strategy: overlay a square div at the inner corner, give it a border-radius
// on the ONE corner that touches the card edge (making it a circle-quadrant shape),
// fill it with the page background. This is 100% reliable cross-browser.
//
// Grid layout:
//   [tl] [tr]
//   [bl] [br]
//
// tl card → arc at bottom-right corner → borderTopLeftRadius: "100%"  (the arc curves away from br corner)
// tr card → arc at bottom-left  corner → borderTopRightRadius: "100%"
// bl card → arc at top-right    corner → borderBottomLeftRadius: "100%"
// br card → arc at top-left     corner → borderBottomRightRadius: "100%"
//
// Diagonal arrows pointing OUTWARD (away from grid center):
//   tl → ↖  tr → ↗  bl → ↙  br → ↘

const ArcCutout = ({ corner, hovered }) => {
  const S = hovered ? 100 : 68;
  const ease = "all 0.38s cubic-bezier(0.4,0,0.2,1)";
  const BG = C.vanilla;

  // Position the square at the card's INNER corner
  const pos = {
    tl: { bottom: 0, right: 0 },
    tr: { bottom: 0, left: 0 },
    bl: { top: 0, right: 0 },
    br: { top: 0, left: 0 },
  }[corner];

  
  const borderRadius = {
    tl: `${S}px 0 0 0`,   // top-left radius only → arc curves toward bottom-right
    tr: `0 ${S}px 0 0`,   // top-right
    bl: `0 0 0 ${S}px`,   // bottom-left
    br: `0 0 ${S}px 0`,  
  }[corner];

  
  const rot = { tl: -135, tr: -45, bl: 135, br: 45 }[corner];

  
  const offset = Math.round(S * 0.28);
  const arrowPos = {
    tl: { bottom: offset, right: offset },
    tr: { bottom: offset, left: offset },
    bl: { top: offset, right: offset },
    br: { top: offset, left: offset },
  }[corner];

  return (
    <Box sx={{ position: "absolute", width: S, height: S, transition: ease, zIndex: 10, ...pos }}>
      {/* The quarter-circle cutout shape */}
      <Box sx={{
        position: "absolute", inset: 0,
        backgroundColor: BG,
        borderRadius,
        transition: ease,
      }} />

      {/* Diagonal arrow at arc midpoint */}
      <Box sx={{ position: "absolute", transition: ease, ...arrowPos }}>
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none"
          style={{ display: "block", transform: `rotate(${rot}deg)` }}>
          {/* Arrow pointing right → */}
          <line x1="1" y1="7.5" x2="13" y2="7.5" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round"/>
          <polyline points="8,2.5 13,7.5 8,12.5" fill="none" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </Box>
    </Box>
  );
};


const ProjectCard = ({ project }) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onClick={() => navigate(project.route)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        backgroundColor: "#d4d0cb",
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        component="img"
        src={project.image}
        alt={project.name}
        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />

      {/* Gradient overlay */}
      <Box
        sx={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.05) 55%, transparent 100%)",
          opacity: hovered ? 1 : 0.5,
          transition: "opacity 0.35s ease",
        }}
      />

      {/* Corner arc */}
      <ArcCutout corner={project.corner} hovered={hovered} />

      {/* Label — left-aligned for tl/bl cards, right-aligned for tr/br cards */}
      <Box sx={{
        position: "absolute", bottom: 18,
        left: (project.corner === "tl" || project.corner === "bl") ? 20 : "auto",
        right: (project.corner === "tr" || project.corner === "br") ? 20 : "auto",
        textAlign: (project.corner === "tl" || project.corner === "bl") ? "left" : "right",
        maxWidth: "60%",
      }}>
        <Typography
          sx={{
            color: "#fff", fontSize: "20px", fontWeight: 500, letterSpacing: "1px", fontFamily: F.heading,
            transform: hovered ? "translateY(-22px)" : "translateY(0)",
            transition: "transform 0.35s ease",
          }}
        >
          {project.name}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.85)", fontSize: "12px", fontFamily: F.body, mt: 0.5, letterSpacing: "0.5px",
            opacity: hovered ? 1 : 0,
            transform: hovered ? "translateY(0)" : "translateY(8px)",
            transition: "opacity 0.35s ease, transform 0.35s ease",
          }}
        >
          {project.description}
        </Typography>
      </Box>
    </Box>
  );
};

const InfoCard = ({ title, description, meta, tint, textColor, borderColor, thumbnail }) => (
  <Box sx={{
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    minHeight: "220px",
    p: "28px",
    backgroundColor: tint,
    border: `1.5px solid ${borderColor}`,
    borderRadius: "6px",
    textDecoration: "none",
    transition: "transform 0.25s ease",
    "&:hover": { transform: "translateY(-4px)" },
  }}>
    {/* Optional background image (like builds) */}
    {thumbnail && (
      <Box sx={{ position: "absolute", inset: 0, backgroundImage: `url(${thumbnail})`, backgroundSize: "cover", backgroundPosition: "center", display: "block" }} />
    )}

    {/* Dark overlay to ensure text contrast */}
    <Box sx={{
      position: "absolute",
      inset: 0,
      background: thumbnail ? "linear-gradient(to top, rgba(68,58,53,0.92) 0%, rgba(68,58,53,0.3) 55%, transparent 100%)" : "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.02) 100%)",
    }} />

    <Box sx={{ position: "relative" }}>
      <Typography sx={{ fontSize: "22px", fontWeight: 500, letterSpacing: "1px", fontFamily: F.heading, color: textColor, mb: 1.2 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: textColor, lineHeight: 1.7, opacity: 0.95 }}>
        {description}
      </Typography>
      <Typography sx={{ fontSize: "12px", fontFamily: F.heading, letterSpacing: "1.2px", color: textColor, mt: 1.8, opacity: 0.75 }}>
        {meta}
      </Typography>
    </Box>
  </Box>
);

// ─── Achievement Carousel ─────────────────────────────────────────────────────

const AchievementCarousel = ({ images }) => {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    if (images.length > 1) {
      timerRef.current = setInterval(() => {
        setCurrent((prev) => (prev + 1) % images.length);
      }, 3000);
    }
  }, [images.length]);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  if (!images || images.length === 0) {
    return (
      <Box sx={{
        width: "100%", aspectRatio: "16/10", backgroundColor: "rgba(255,255,255,0.06)",
        borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: C.taupe, opacity: 0.5 }}>No images</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", aspectRatio: "16/10", borderRadius: "8px", overflow: "hidden", backgroundColor: "rgba(255,255,255,0.06)" }}>
      {images.map((img, i) => (
        <Box
          key={i}
          component="img"
          src={img}
          alt=""
          sx={{
            position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover",
            opacity: i === current ? 1 : 0,
            transition: "opacity 0.6s cubic-bezier(0.4,0,0.2,1)",
          }}
        />
      ))}

      {/* Dots */}
      {images.length > 1 && (
        <Box sx={{ position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)", display: "flex", gap: "6px" }}>
          {images.map((_, i) => (
            <Box
              key={i}
              onClick={() => { setCurrent(i); startTimer(); }}
              sx={{
                width: i === current ? 18 : 6, height: 6, borderRadius: "3px",
                backgroundColor: i === current ? C.vanilla : "rgba(248,244,238,0.4)",
                transition: "all 0.3s ease", cursor: "pointer",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Timeline Stop ────────────────────────────────────────────────────────────

const TimelineStop = ({ achievement, index, isLeft }) => (
  <Box sx={{
    display: "flex",
    flexDirection: { xs: "column", md: isLeft ? "row" : "row-reverse" },
    alignItems: { xs: "center", md: "flex-start" },
    gap: { xs: 0, md: "40px" },
    width: "100%",
    position: "relative",
  }}>
    {/* Content side */}
    <Box sx={{
      flex: 1,
      textAlign: { xs: "center", md: isLeft ? "right" : "left" },
      order: { xs: 2, md: 0 },
      mt: { xs: 2, md: 0 },
    }}>
      <AchievementCarousel images={achievement.gallery || []} />
      <Typography sx={{
        fontSize: "20px", fontWeight: 500, letterSpacing: "1px", fontFamily: F.heading,
        color: C.vanilla, mt: 2,
      }}>
        {achievement.title}
      </Typography>
      <Typography sx={{
        fontSize: "13px", fontFamily: F.body, color: C.vanilla, lineHeight: 1.7,
        opacity: 0.85, mt: 0.8,
      }}>
        {achievement.description}
      </Typography>
      <Typography sx={{
        fontSize: "12px", fontFamily: F.heading, letterSpacing: "1.2px",
        color: C.taupe, mt: 1.2,
      }}>
        {achievement.meta}
      </Typography>
    </Box>

    {/* Center: numbered circle (visible only on md+, xs has its own inline circle) */}
    <Box sx={{
      display: { xs: "none", md: "flex" },
      flexShrink: 0, width: 44, height: 44, borderRadius: "50%",
      border: `2px solid ${C.taupe}`, backgroundColor: C.umber,
      alignItems: "center", justifyContent: "center",
      position: "relative", zIndex: 2,
    }}>
      <Typography sx={{ fontSize: "15px", fontWeight: 600, fontFamily: F.heading, color: C.vanilla }}>
        {index + 1}
      </Typography>
    </Box>

    {/* Empty spacer for the other side */}
    <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />
  </Box>
);

// ─── Placeholder Achievements (remove once real data is added via admin) ──────

const PLACEHOLDER_ACHIEVEMENTS = [
  {
    _id: "placeholder-1",
    title: "Achievement Title 1",
    description: "Brief description of this achievement and what it involved.",
    meta: "2025 • Category",
    gallery: [],
  },
  {
    _id: "placeholder-2",
    title: "Achievement Title 2",
    description: "Brief description of this achievement and what it involved.",
    meta: "2025 • Category",
    gallery: [],
  },
  {
    _id: "placeholder-3",
    title: "Achievement Title 3",
    description: "Brief description of this achievement and what it involved.",
    meta: "2026 • Category",
    gallery: [],
  },
  {
    _id: "placeholder-4",
    title: "Achievement Title 4",
    description: "Brief description of this achievement and what it involved.",
    meta: "2026 • Category",
    gallery: [],
  },
];

// ─── Landing Page ─────────────────────────────────────────────────────────────

const LandingPage = () => {
  const { builds } = useBuilds();
  const { designs } = useDesigns();
  const { achievements: apiAchievements } = useAchievements();
  const { certifications } = useCertifications();
  const achievements = apiAchievements && apiAchievements.length > 0 ? apiAchievements : PLACEHOLDER_ACHIEVEMENTS;
  const dynamicResumeUrl = useResume();
  const photographyDesign = designs.find((d) => d.category === "Photography");
  const previewPhotos = (photographyDesign?.gallery || []).slice(0, 8);
  const previewHeights = [480, 160, 160, 480, 320, 320, 160, 480];

  useEffect(() => {
    trackVisit();
  }, []);

  return (
    <>
      <Box sx={{ backgroundColor: C.vanilla, width: "100%" }}>

      {/* ===== HERO ===== */}
      <Box sx={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        overflow: "hidden", backgroundColor: C.vanilla,
        backgroundImage: `url(${heroBg})`, backgroundSize: "cover", backgroundPosition: "center",
      }}>
        {/* ── Top bar ── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", p: { xs: "20px", md: "30px" } }}>
          <Box>
            <Typography sx={{ fontSize: "14px", mb: 0.5, fontFamily: F.body, color: C.vanilla, fontWeight: 700 }}>{"<HJ>"}</Typography>
            <Typography sx={{ fontSize: "13px", letterSpacing: "3px", fontFamily: F.heading, borderBottom: `1px solid ${C.vanilla}`, display: "inline-block", color: C.vanilla, fontWeight: 700 }}>
              PORTFOLIO 2026
            </Typography>
          </Box>

          {/* Resume spinning badge */}
          <Box component="a" href={dynamicResumeUrl || "/Haridharshini_Resume.pdf"} download="Haridharshini_Resume.pdf" onClick={trackResumeDownload}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", width: 76, height: 76, cursor: "pointer", textDecoration: "none", flexShrink: 0 }}>
            <Box sx={{
              position: "absolute", width: "100%", height: "100%",
              animation: "spinSlow 10s linear infinite",
              "@keyframes spinSlow": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
            }}>
              <svg width="76" height="76" viewBox="0 0 76 76">
                <defs><path id="resumeCircle" d="M 38,38 m -27,0 a 27,27 0 1,1 54,0 a 27,27 0 1,1 -54,0" /></defs>
                <text fill={C.vanilla} fontSize="8.2" letterSpacing="3.6" fontFamily="'Montserrat', sans-serif" fontWeight="500">
                  <textPath href="#resumeCircle">RESUME • DOWNLOAD •</textPath>
                </text>
              </svg>
            </Box>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.vanilla} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><polyline points="5 12 12 19 19 12"/>
            </svg>
          </Box>
        </Box>

        {/* ── Spacer pushes bottom content down ── */}
        <Box sx={{ flex: 1 }} />

        {/* ── Bottom row: social icons left, big title right ── */}
        <Box sx={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-end",
          px: { xs: "20px", md: "30px" }, pb: { xs: "20px", md: "16px" },
        }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, gap: 1.5 }}>
            {[
              { href: "https://github.com/Haridharshini510",                        icon: <GitHubIcon sx={{ fontSize: 18 }} /> },
              { href: "https://www.linkedin.com/in/haridharshini-jayaraj-45a704306/", icon: <LinkedInIcon sx={{ fontSize: 18 }} /> },
              { href: "https://x.com/haridharshini05",                              icon: <XIcon /> },
            ].map(({ href, icon }, i) => (
              <IconButton key={i} component="a" href={href} target="_blank" rel="noopener noreferrer"
                sx={{ width: 36, height: 36, backgroundColor: C.taupe, color: C.vanilla, "&:hover": { backgroundColor: C.umber, color: C.vanilla }, transition: "all 0.2s ease" }}>
                {icon}
              </IconButton>
            ))}
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: { xs: "52px", sm: "72px", md: "100px" }, letterSpacing: "6px", lineHeight: 0.9, color: C.vanilla }}>
              <Box component="span" sx={{ fontFamily: F.serif, fontWeight: 400, letterSpacing: "0px", fontSize: { xs: "44px", sm: "60px", md: "80px" } }}> DESIGNER</Box>
            </Typography>
            <Typography sx={{ fontSize: { xs: "64px", sm: "90px", md: "120px" }, letterSpacing: "6px", lineHeight: 0.9, color: C.vanilla }}>
              <Box component="span" sx={{ fontFamily: F.display, fontWeight: 400 }}>& DEVELOPER</Box>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ===== MARQUEE ===== */}
      <Box sx={{ borderTop: `1px solid ${C.vanilla}`, borderBottom: `1px solid ${C.vanilla}`, pt: 4, pb: 1.2, overflow: "hidden", whiteSpace: "nowrap", backgroundColor: C.vanilla }}>
        <Box sx={{
          display: "inline-flex", gap: 5,
          animation: "marquee 45s linear infinite",
          "@keyframes marquee": { "0%": { transform: "translateX(0)" }, "100%": { transform: "translateX(-50%)" } },
        }}>
          {[...marqueeSkills, ...marqueeSkills, ...marqueeSkills, ...marqueeSkills].map((skill, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: C.umber, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "13px", letterSpacing: "3px", color: C.umber }}>{skill}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ===== ABOUT ME ===== */}
      <Box sx={{ px: { xs: "16px", md: "30px" }, pt: 6, pb: 6, backgroundColor: C.vanilla }}>
        <Typography sx={{ fontSize: { xs: "36px", md: "52px" }, fontWeight: 400, letterSpacing: "2px", mb: 5, color: C.black }}>/about me</Typography>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: "32px", md: "60px" }, alignItems: "flex-start" }}>
          <Box sx={{ flexShrink: 0, width: "300px" }}>
            <Box sx={{ width: "300px", height: "340px", backgroundColor: C.softDune, mb: 3, borderRadius: "4px", overflow: "hidden" }}>
              <Box component="img" src={aboutImage} alt="Haridharshini Jayaraj" sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: 16, color: C.umber }} />
              <Typography sx={{ fontSize: "14px", fontFamily: F.body, color: C.black }}>Haridharshini Jayaraj</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <MailOutlineIcon sx={{ fontSize: 16, color: C.umber }} />
              <Typography sx={{ fontSize: "14px", fontFamily: F.body, color: C.umber }}>haridharshinijayaraj@gmail.com</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px", lineHeight: 1.8, fontFamily: F.body, color: C.umber, mb: 3 }}>
               I'm someone who enjoys creating things—whether that's designing an interface, building a web application, or capturing a moment through a camera lens.

I love the process of taking an idea from "what if?" to "this actually works." Sometimes that means sketching wireframes, sometimes it's debugging code and sometimes it's chasing good lighting for the perfect photograph.
            </Typography>
            <Typography sx={{ fontSize: "15px", lineHeight: 1.8, fontFamily: F.body, color: C.umber, mb: 5 }}>
I'm currently studying Computer Science while exploring the worlds of development, design, and visual storytelling. I enjoy learning new technologies, experimenting with ideas, and building projects that are not only functional but also enjoyable to use. At the end of the day, I just like making things that people genuinely enjoy interacting with.            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 5, columnGap: 4 }}>
              <Box>
                <Typography sx={{ fontSize: "14px", letterSpacing: "3px", fontWeight: 500, fontFamily: F.heading, mb: 2, borderBottom: `1px solid ${C.taupe}`, pb: 1, color: C.black }}>EDUCATION</Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: C.taupe, minWidth: "70px" }}>2024–28</Typography>
                  <Box>
                    <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: F.body, color: C.black }}>B.E Computer Science</Typography>
                    <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: C.umber }}>Saveetha Engineering College</Typography>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "14px", letterSpacing: "3px", fontWeight: 500, fontFamily: F.heading, mb: 2, borderBottom: `1px solid ${C.taupe}`, pb: 1, color: C.black }}>EXPERIENCE</Typography>
                <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                  <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: C.taupe, minWidth: "46px" }}>2025</Typography>
                  <Box>
                    <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: F.body, color: C.black }}>UI/UX Design Intern</Typography>
                    <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: C.umber }}>DY Interiors</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: C.taupe, minWidth: "46px" }}>2026</Typography>
                  <Box>
                    <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: F.body, color: C.black }}>Freelancer</Typography>
                    <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: C.umber }}>Web Development</Typography>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "14px", letterSpacing: "3px", fontWeight: 500, fontFamily: F.heading, mb: 2, borderBottom: `1px solid ${C.taupe}`, pb: 1, color: C.black }}>SOFTWARES</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {[
                    { icon: <MatlabIcon />, label: "Matlab" },
                    { icon: <FigmaIcon />, label: "Figma" },
                    { icon: <AdobeIcon />, label: "Adobe Suite" },
                    { icon: <CanvaIcon />, label: "Canva" },
                  ].map(({ icon, label }) => (
                    <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.5, border: `1px solid ${C.taupe}`, borderRadius: "20px", backgroundColor: C.softDune }}>
                      {icon}
                      <Typography sx={{ fontSize: "12px", fontFamily: F.body, color: C.umber }}>{label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "14px", letterSpacing: "3px", fontWeight: 500, fontFamily: F.heading, mb: 2, borderBottom: `1px solid ${C.taupe}`, pb: 1, color: C.black }}>PERSONAL SKILLS</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {["Team Player", "Adaptability", "Time Management", "Attention to Detail", "Creative Thinking", "Communication"].map((s) => (
                    <Box key={s} sx={{ px: 1.5, py: 0.5, border: `1px solid ${C.taupe}`, borderRadius: "20px", backgroundColor: C.softDune }}>
                      <Typography sx={{ fontSize: "12px", fontFamily: F.body, color: C.umber }}>{s}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ===== BUILDS ===== */}
      <Box sx={{ px: { xs: "16px", md: "30px" }, pt: 6, pb: 7, backgroundColor: C.umber }}>
        <Typography sx={{ fontSize: { xs: "36px", md: "52px" }, fontWeight: 400, letterSpacing: "2px", mb: 4, color: C.vanilla }}>/builds</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gridAutoRows: "220px", gap: { xs: "16px", md: "40px" } }}>
          {builds.map((b) => (
            <Box
              key={b._id}
              component="a"
              href={b.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackBuildClick(b._id)}
              sx={{
                position: "relative",
                overflow: "hidden",
                display: "flex", flexDirection: "column", justifyContent: "flex-end",
                p: "28px",
                backgroundColor: "rgba(255,255,255,0.06)",
                ...(b.thumbnail && {
                  backgroundImage: `url(${b.thumbnail})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }),
                border: `1.5px solid ${C.vanilla}`,
                borderRadius: "6px",
                textDecoration: "none",
                transition: "transform 0.25s ease",
                "&:hover": { transform: "translateY(-4px)" },
              }}
            >
              <Box sx={{
                position: "absolute", inset: 0,
                background: b.thumbnail
                  ? "linear-gradient(to top, rgba(68,58,53,0.92) 0%, rgba(68,58,53,0.3) 55%, transparent 100%)"
                  : "none",
              }} />
              <Box sx={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                <Box>
                  <Typography sx={{ fontSize: "22px", fontWeight: 500, letterSpacing: "1px", fontFamily: F.heading, color: C.vanilla }}>
                    {b.title}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: C.taupe, mt: 0.5 }}>
                    {b.shortDescription}
                  </Typography>
                </Box>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.vanilla} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginLeft: 12 }}>
                  <line x1="7" y1="17" x2="17" y2="7"/>
                  <polyline points="7 7 17 7 17 17"/>
                </svg>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ===== FEATURED PROJECTS ===== */}
      <Box sx={{ px: { xs: "16px", md: "30px" }, pt: 5, pb: 5, backgroundColor: C.vanilla }}>
        <Typography sx={{ fontSize: { xs: "36px", md: "52px" }, fontWeight: 400, letterSpacing: "2px", mb: 4, color: C.black }}>/designs</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: "24px", md: "40px" }, backgroundColor: C.vanilla, overflow: "hidden" }}>
          {projects.map((p) => (
            <Box key={p.id} sx={{ height: { xs: "220px", md: "280px" } }}>
              <ProjectCard project={p} />
            </Box>
          ))}
        </Box>
      </Box>
      

      {/* ===== FEATS (ACHIEVEMENTS TIMELINE) ===== */}
      {achievements && achievements.length > 0 && (
        <Box sx={{ px: { xs: "16px", md: "30px" }, pt: 6, pb: 7, backgroundColor: C.umber }}>
          <Typography sx={{ fontSize: { xs: "36px", md: "52px" }, fontWeight: 400, letterSpacing: "2px", mb: 6, color: C.vanilla }}>/feats</Typography>

          {/* Timeline container */}
          <Box sx={{ position: "relative", maxWidth: "900px", mx: "auto" }}>

            {/* Central vertical line (md+ only) */}
            <Box sx={{
              display: { xs: "none", md: "block" },
              position: "absolute", left: "50%", top: 0, bottom: 0,
              width: "2px", backgroundColor: C.taupe, opacity: 0.4,
              transform: "translateX(-50%)",
            }} />

            {/* Timeline stops */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "16px", md: "20px" } }}>
              {achievements.map((achievement, i) => (
                <Box key={achievement._id} sx={{ position: "relative" }}>

                  {/* Mobile numbered circle */}
                  <Box sx={{
                    display: { xs: "flex", md: "none" },
                    width: 36, height: 36, borderRadius: "50%",
                    border: `2px solid ${C.taupe}`, backgroundColor: C.umber,
                    alignItems: "center", justifyContent: "center",
                    mx: "auto", mb: 2, position: "relative", zIndex: 2,
                  }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, fontFamily: F.heading, color: C.vanilla }}>
                      {i + 1}
                    </Typography>
                  </Box>

                  <TimelineStop achievement={achievement} index={i} isLeft={i % 2 === 0} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* ===== PHOTOGRAPHY / MASONRY ===== */}
      <Box sx={{ px: { xs: "16px", md: "30px" }, pt: 4, pb: 10, backgroundColor: C.vanilla }}>
        <Typography sx={{ fontSize: "17px", lineHeight: 1.75, mb: 4,textAlign: "center", fontFamily: F.body, color: C.black }}>
          Beyond design, I capture moments through photography.<br />
          Visual storytelling influences how I create meaningful visual experiences.
        </Typography>

        {/* Masonry — 4 cols, sourced from the "Captured Moments" design's gallery */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: "12px" }}>
          {[0, 1, 2, 3].map((col) => (
            <Box key={col} sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {[previewPhotos[col * 2], previewPhotos[col * 2 + 1]]
                .map((img, k) => ({ img, h: previewHeights[col * 2 + k] }))
                .filter(({ img }) => img)
                .map(({ img, h }, k) => (
                  <Box key={k} sx={{ height: `${h}px`, borderRadius: "16px", overflow: "hidden", backgroundColor: C.taupe,
                    "& img": { transition: "transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94)" },
                    "&:hover img": { transform: "scale(1.07)" },
                  }}>
                    <Box component="img" src={img} alt=""
                      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </Box>
                ))}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <Box
            component={Link}
            to="/photography"
            sx={{
              px: 4, py: 1.3,
              border: `1px solid ${C.black}`,
              borderRadius: "30px",
              fontSize: "13px", letterSpacing: "2px", fontFamily: F.heading,
              color: C.black, textDecoration: "none",
              transition: "background-color 0.2s ease, color 0.2s ease",
              "&:hover": { backgroundColor: C.black, color: C.vanilla },
            }}
          >
            VIEW ALL
          </Box>
        </Box>
      </Box>

      {/* ===== CERTIFICATIONS ===== */}
      {certifications && certifications.length > 0 && (
        <Box sx={{ px: { xs: "16px", md: "30px" }, pt: 6, pb: 7, backgroundColor: C.vanilla }}>
          <Typography sx={{ fontSize: { xs: "36px", md: "52px" }, fontWeight: 400, letterSpacing: "2px", mb: 4, color: C.black }}>/certifications</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: { xs: "16px", md: "24px" } }}>
            {certifications.map((item) => (
              <InfoCard key={item._id} title={item.title} description={item.description} meta={item.meta} tint={C.softDune} textColor={C.umber} borderColor={C.black} thumbnail={item.thumbnail} />
            ))}
          </Box>
        </Box>
      )}

      {/* ===== FOOTER ===== */}
      <Box sx={{ backgroundColor: C.umber }}>
        {/* Top row: icons + nav — with padding */}
        <Box sx={{ px: "30px", pt: 5, pb: 4, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid rgba(255,255,255,0.1)` }}>
          {/* Social icon dots */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            {[
              { href: "https://www.linkedin.com/in/haridharshini-jayaraj-45a704306/", label: "LinkedIn", svg: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/>
                </svg>
              )},
              { href: "https://github.com/Haridharshini510", label: "GitHub", svg: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
                </svg>
              )},
              { href: "https://x.com/haridharshini05", label: "X", svg: <XIcon /> },
            ].map(({ href, label, svg }) => (
              <Box key={label} component="a" href={href} target="_blank" rel="noopener noreferrer" title={label}
                sx={{
                  width: 36, height: 36, borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: C.softDune, textDecoration: "none",
                  transition: "background-color 0.2s ease, color 0.2s ease",
                  "&:hover": { backgroundColor: C.softDune, color: C.umber },
                }}>
                {svg}
              </Box>
            ))}
          </Box>

          {/* Drop a message CTA */}
          <Box
            component={Link}
            to="/contact"
            sx={{
              px: 3, py: 1.2,
              border: `1px solid ${C.softDune}`,
              borderRadius: "30px",
              fontSize: "13px", letterSpacing: "1px", fontFamily: F.heading,
              color: C.softDune, textDecoration: "none",
              transition: "background-color 0.2s ease, color 0.2s ease",
              "&:hover": { backgroundColor: C.softDune, color: C.umber },
            }}
          >
            Drop a message to collaborate!
          </Box>
        </Box>

        {/* LETS CONNECT — full width, white text on umber bg */}
        <Box sx={{ width: "100%", overflow: "hidden", px: "30px", pt: 3, pb: 2 }}>
          <Typography
            sx={{
              display: "block",
              fontSize: "calc((100vw - 60px) / 8.8)",
              fontWeight: 500,
              letterSpacing: "0.07em",
              lineHeight: 0.95,
              fontFamily: F.heading,
              color: C.vanilla,
              whiteSpace: "nowrap",
              userSelect: "none",
            }}
          >
            LETS CONNECT
          </Typography>
        </Box>
      </Box>

    </Box>
    </>
  );
};

export default LandingPage;